// Plan tier definitions for the GHL marketplace integration.
// Tier slug arrives in the URL: /:companyId/:planId/...
// Unknown planId falls back to "starter" (there is no free plan).

export type Tier = "starter" | "pro";

export interface Features {
  // Limits — every number is concrete (Pro uses a very large number for "unlimited").
  maxLinks: number; // total active short links
  linksPerMonth: number; // monthly creation cap (30-day rolling window)
  dataRetentionDays: number; // click + scan history kept this many days

  // Link options
  customSlugs: boolean;
  utm: boolean;
  expiration: boolean; // user-controlled expiration override
  password: boolean;
  geoTargeting: boolean; // country-based redirect rules

  // QR Studio
  qrColors: boolean;
  qrShapes: boolean;
  qrLogo: boolean;
  qrAdvanced: boolean;

  // Organization
  campaigns: boolean;

  // Distribution
  customDomain: boolean;
  maxDomains: number; // branded domains allowed (Starter 1, Pro 5)
  csvExport: boolean;

  // Landing pages (link-in-bio)
  landingPages: boolean;
  maxPages: number;

  // Advanced routing
  deviceTargeting: boolean; // device/OS-based redirect rules
  abTesting: boolean; // weighted A/B rotation
}

// Sentinel for "unlimited" — kept concrete so limit checks stay simple.
export const UNLIMITED = 1_000_000;

export const PLAN_LABELS: Record<Tier, string> = {
  starter: "Starter",
  pro: "Pro",
};

export const PLAN_FEATURES: Record<Tier, Features> = {
  starter: {
    maxLinks: 1000,
    linksPerMonth: 1000,
    dataRetentionDays: 180,
    customSlugs: true,
    utm: true,
    expiration: true,
    password: false, // Pro only
    geoTargeting: false, // Pro only
    qrColors: true,
    qrShapes: true,
    qrLogo: true,
    qrAdvanced: true,
    campaigns: true,
    customDomain: true,
    maxDomains: 1,
    csvExport: true,
    landingPages: true,
    maxPages: 50,
    deviceTargeting: false, // Pro only
    abTesting: false, // Pro only
  },
  pro: {
    maxLinks: UNLIMITED,
    linksPerMonth: UNLIMITED,
    dataRetentionDays: 365,
    customSlugs: true,
    utm: true,
    expiration: true,
    password: true,
    geoTargeting: true,
    qrColors: true,
    qrShapes: true,
    qrLogo: true,
    qrAdvanced: true,
    campaigns: true,
    customDomain: true,
    maxDomains: 5,
    csvExport: true,
    landingPages: true,
    maxPages: 250,
    deviceTargeting: true,
    abTesting: true,
  },
};

// First tier that unlocks each feature — used for "Available in {tier}" hints.
export const FEATURE_MIN_TIER: Record<keyof Features, Tier> = {
  maxLinks: "starter",
  linksPerMonth: "starter",
  dataRetentionDays: "starter",
  customSlugs: "starter",
  utm: "starter",
  expiration: "starter",
  password: "pro",
  geoTargeting: "pro",
  qrColors: "starter",
  qrShapes: "starter",
  qrLogo: "starter",
  qrAdvanced: "starter",
  campaigns: "starter",
  customDomain: "starter",
  maxDomains: "starter",
  csvExport: "starter",
  landingPages: "starter",
  maxPages: "starter",
  deviceTargeting: "pro",
  abTesting: "pro",
};

export const normalizeTier = (planId?: string): Tier => {
  const id = (planId || "").toLowerCase();
  if (id === "starter" || id === "pro") return id;
  return "starter";
};

// Tier order for comparison (left → right in the upgrade modal).
export const TIER_ORDER: Tier[] = ["starter", "pro"];

export interface PlanCardFeature {
  label: string;
  included: boolean;
  emphasize?: boolean;
}

export interface PlanCard {
  tier: Tier;
  name: string;
  tagline: string;
  cta: string;
  features: PlanCardFeature[];
}

export const PLAN_CARDS: PlanCard[] = [
  {
    tier: "starter",
    name: "Starter",
    tagline: "Branded links, full tracking, and CRM automation.",
    cta: "Switch to Starter",
    features: [
      { label: "1,000 active short links", included: true, emphasize: true },
      { label: "180 days of click & scan data", included: true, emphasize: true },
      { label: "Tag the contact on click", included: true, emphasize: true },
      { label: "Per-contact click analytics", included: true, emphasize: true },
      { label: "CRM funnel / website destinations", included: true, emphasize: true },
      { label: "Custom slugs, UTM presets & scheduling", included: true },
      { label: "Landing pages with lead forms & QR Studio", included: true },
      { label: "Bulk import, duplicate links & deep links", included: true },
      { label: "QR templates, frames & batch export", included: true },
      { label: "1 custom branded domain", included: true },
      { label: "Password protection", included: false },
      { label: "Country & device targeting + A/B", included: false },
    ],
  },
  {
    tier: "pro",
    name: "Pro",
    tagline: "For agencies running at scale.",
    cta: "Upgrade to Pro",
    features: [
      { label: "Everything in Starter", included: true, emphasize: true },
      { label: "Unlimited short links", included: true, emphasize: true },
      { label: "365 days of click & scan data", included: true, emphasize: true },
      { label: "Up to 5 branded domains", included: true, emphasize: true },
      { label: "Password-protected links", included: true, emphasize: true },
      { label: "Country & device targeting", included: true, emphasize: true },
      { label: "A/B link rotation", included: true, emphasize: true },
      { label: "Landing pages (up to 250)", included: true },
    ],
  },
];
